import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { useMutation } from '@apollo/client'
import { useStripe } from '@stripe/react-stripe-js'
import pick from 'lodash/pick'

import { setCurrentUser } from 'store/modules/CurrentUser'

import { closeErrorNotifications, showErrors } from 'helpers/errors'
import { combineDateAndTime } from 'helpers/date'
import { getGoogleAnalyticsTracker } from 'helpers/googleAnalytics'
import { parseBudgetRange } from 'helpers/number'
import { sanitizeFormValues, showSanitizedErrors } from 'components/ContactRequestFormComponents/helpers'

import Form from '../components/Form'

import {
  CREATE_USER_MUTATION,
  CREATE_PROJECT_MUTATION,
  CREATE_CONTACT_REQUEST_MUTATION,
  UPDATE_PROJECT_OFFER_MUTATION,
} from '../graphql'

const mapDispatchToProps = (dispatch) => ({
  setUserHasAccessToken: () => dispatch(setCurrentUser({ hasAccessToken: true })),
})

const EventRoom = (props) => {
  const {
    provider,
    intl,
    initialData,
    setUserHasAccessToken,
    currentUser,
    onSuccessCallback,
    isWithinPayablePeriod,
  } = props

  const [createUser, { loading: userSubmitting }] = useMutation(CREATE_USER_MUTATION)
  const [createProject, { loading: projectSubmitting }] = useMutation(CREATE_PROJECT_MUTATION)
  const [createContactRequest, { loading: contactRequestSubmitting }] = useMutation(CREATE_CONTACT_REQUEST_MUTATION)
  const [updateProjectOffer, { loading: projectOfferSubmitting }] = useMutation(UPDATE_PROJECT_OFFER_MUTATION)

  const stripe = provider.isDirectPayable && isWithinPayablePeriod ? useStripe() : {}
  const disableSubmit = !stripe

  const createUserWrapper = (createUserData = {}) => {
    if (currentUser) {
      return new Promise((resolve) => resolve(createUserData))
    }

    return createUser({ variables: createUserData })
  }

  const submitting = userSubmitting || projectSubmitting || contactRequestSubmitting || projectOfferSubmitting

  const submitForm = async (formData) => {
    const success = await createUserWrapper(pick(formData, ['company', 'name', 'email', 'phone']))
      .then((_userResult) => {
        const budgetRange = parseBudgetRange(formData.eventBudget)

        let eventTimeRanges

        if (formData.requestedDates?.[0] && formData.timesStartsAt && formData.timesEndsAt) {
          eventTimeRanges = {
            startsAt: combineDateAndTime(formData.requestedDates[0], formData.timesStartsAt).format(),
            endsAt: combineDateAndTime(formData.requestedDates[0], formData.timesEndsAt).format(),
          }
        }

        return createProject({
          variables: {
            ...pick(formData, ['email', 'eventCategoryId', 'roomType']),
            guestCount: parseInt(formData.numberOfPersons),
            budgetMax: budgetRange[1],
            providerId: provider.id,
            pendingRequest: provider.isDirectPayable,
            eventTimeRanges,
          },
        })
      }).then((projectResult) => {
        const ga = getGoogleAnalyticsTracker()

        return createContactRequest({
          variables: {
            providerId: provider.id,
            projectId: projectResult.data.createProject.project.id,
            availabilityId: formData.availabilityId,
            consent: formData.consent,
            providerTermsConditionsConsent: formData.providerTermsConditionsConsent,
            newsletter: formData.newsletter,
            googleAnalyticsClientId: ga && ga.get('clientId'),
            forNewProject: true,
          },
        })
      }).then(({ data }) => {
        let disableSuccessForward = false

        if (formData.grossPrice) {
          updateProjectOffer({
            variables: {
              ticketNumber: data.createContactRequest.contactRequest.ticketNumber,
              venueHireCosts: formData.grossPrice,
              isGross: true,
            },
          })
        }

        if (provider.isDirectPayable && data.createContactRequest.paymentSession?.id && isWithinPayablePeriod) {
          disableSuccessForward = true

          const ga = getGoogleAnalyticsTracker()

          let isRedirected = false

          const redirectCallback = () => {
            if (!isRedirected) {
              isRedirected = true
              stripe.redirectToCheckout({ sessionId: data.createContactRequest.paymentSession.id })
            }
          }

          ga && ga.send('pageview', location.pathname.replace('book', 'checkout'), {
            hitCallback: redirectCallback,
          })

          setTimeout(redirectCallback, 1000)
        }

        onSuccessCallback(data.createContactRequest.contactRequest, disableSuccessForward)

        return true
      }).catch((errors) => {
        if (errors?.name === 'IntegrationError') {
          showErrors(intl.formatMessage({ id: 'contactRequest.errors.redirectToPaymentFailed' }))

          return true
        }

        showSanitizedErrors(
          errors.graphQLErrors[0]?.extensions?.errors,
          intl,
        )

        return false
      })

    if (success) {
      setUserHasAccessToken()
    }
  }

  const submitFormAction = (values) => {
    closeErrorNotifications()
    submitForm(sanitizeFormValues(values))
  }

  const translationKeys = {
    headline: 'contactRequest.headline.requestToBook',
    ctaButton: 'contactRequest.ctaBook',
    ctaHint: null,
  }

  if (provider.isInstantBookable) {
    translationKeys.headline = 'contactRequest.headline.instantBook'
  }

  if (provider.isDirectPayable && isWithinPayablePeriod) {
    translationKeys.ctaButton = 'contactRequest.ctaPay'
    translationKeys.ctaHint = 'contactRequest.paymentCtaHint'
  }

  if (!provider.isFixedBooking) {
    translationKeys.headline = 'contactRequest.headline.rfp'
    translationKeys.ctaButton = 'contactRequest.ctaRequest'
  }

  return (
    <Form
      initialData={initialData}
      provider={provider}
      currentUser={currentUser}
      submitForm={submitFormAction}
      isSubmitting={submitting}
      disableSubmit={disableSubmit}
      headlineTranslationKey={translationKeys.headline}
      ctaTranslationKey={translationKeys.ctaButton}
      ctaHintTranslationKey={translationKeys.ctaHint}
    />
  )
}

EventRoom.propTypes = {
  intl: PropTypes.object.isRequired,
  initialData: PropTypes.shape({
    requestedDates: PropTypes.arrayOf(PropTypes.string),
  }),
  currentUser: PropTypes.object,
  setUserHasAccessToken: PropTypes.func.isRequired,
  onSuccessCallback: PropTypes.func.isRequired,
  isWithinPayablePeriod: PropTypes.bool,
  provider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isInstantBookable: PropTypes.bool,
    isDirectPayable: PropTypes.bool,
    isFixedBooking: PropTypes.bool,
  }).isRequired,
}

export default connect(undefined, mapDispatchToProps)(injectIntl(EventRoom))
